// BlogPost.js - Single blog post view
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogApi } from '../../Services/Api';
import styles from './BlogPost.module.css';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await blogApi.getPost(slug);
        setPost(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Post not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [slug]);

  const handleLike = async () => {
    try {
      const response = await blogApi.likePost(post.id);
      setPost({
        ...post,
        is_liked: response.data.liked,
        like_count: response.data.like_count
      });
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatContent = (content) => {
    // Split by paragraphs and render with proper spacing
    return content.split('\n\n').map((paragraph, index) => (
      <p key={index} className={styles.paragraph}>
        {paragraph.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < paragraph.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    ));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <span className={styles.errorIcon}>😕</span>
          <h2>{error}</h2>
          <button onClick={() => navigate('/blog')}>Back to Blog</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Back button */}
      <button className={styles.backButton} onClick={() => navigate('/blog')}>
        ← Back to Blog
      </button>

      {/* Article */}
      <article className={styles.article}>
        {/* Header */}
        <header className={styles.header}>
          {post.category && (
            <span className={styles.category}>
              {post.category.icon} {post.category.name}
            </span>
          )}
          
          <h1 className={styles.title}>{post.title}</h1>
          
          <div className={styles.meta}>
            <div className={styles.author}>
              <span className={styles.authorAvatar}>
                {post.author.username.charAt(0).toUpperCase()}
              </span>
              <div>
                <span className={styles.authorName}>{post.author.username}</span>
                <span className={styles.date}>{formatDate(post.published_at || post.created_at)}</span>
              </div>
            </div>
            

          </div>
        </header>

        {/* Content */}
        <div className={styles.content}>
          {formatContent(post.content)}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button 
            className={`${styles.likeButton} ${post.is_liked ? styles.liked : ''}`}
            onClick={handleLike}
          >
            {post.is_liked ? '❤️' : '🤍'} {post.like_count} {post.like_count === 1 ? 'Like' : 'Likes'}
          </button>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
